import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGuest } from '../context/GuestContext';
import placeService from '../services/placeService';
import ThemeSelector from './ThemeSelector';
import LegalModal from './LegalModal';
import ProfileModal from './ProfileModal';
import './HamburgerMenu.css';

const CATEGORIES = ['Music', 'Food & Beverage', 'Nature', 'Events & Shows', 'Sports', 'Landmarks', 'Other'];

const HamburgerMenu = ({ onFilterChange, onReset, onRestore, onPlaceAdded, onDeleteAll }) => {
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [myPlacesOnly, setMyPlacesOnly] = useState(false);
  const [sortByCategory, setSortByCategory] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [directions, setDirections] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { logout, user } = useAuth();
  const { theme } = useTheme();
  const { isGuest } = useGuest();
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = () => {
    onFilterChange(selectedStatus, selectedCategory, myPlacesOnly, sortByCategory);
    setOpen(false);
  };

  const handleReset = () => {
    setMyPlacesOnly(false);
    setSortByCategory(false);
    setSelectedStatus('');
    setSelectedCategory('');
    onReset();
    setOpen(false);
  };

  const handleRestore = () => {
    onRestore();
    setOpen(false);
  };

  const handleDeleteAllConfirm = () => {
    onDeleteAll();
    setShowDeleteConfirm(false);
    setOpen(false);
  };

  const handleAddPlace = async () => {
    if (!title || !category) {
      setError('Please enter a place name and select a category.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const newPlace = {
        title, category, address, description, directions,
        photoUrl: '', status: 'unvisited', userAdded: true,
        userId: user?.userId || user?.id || 1
      };
      const token = JSON.parse(sessionStorage.getItem('user'))?.token;
      await placeService.addUserPlace(token, newPlace);
      setTitle(''); setCategory(''); setAddress('');
      setDescription(''); setDirections('');
      setShowManual(false);
      if (onPlaceAdded) onPlaceAdded();
    } catch (err) {
      console.error('Failed to add place:', err);
      setError('Failed to save place. Please try again.');
    } finally {
      setSaving(false);
    }
  }; 

  const inputStyle = {
    width: '100%', padding: '12px 14px', marginBottom: '12px',
    border: `2px solid ${theme.border}`, borderRadius: '8px',
    backgroundColor: theme.background, color: theme.text,
    fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none',
  };

  const checkboxRow = (id, checked, onChange, label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <input type="checkbox" id={id} checked={checked} onChange={onChange}
        style={{ accentColor: theme.buttonPrimary, width: '16px', height: '16px' }} />
      <label htmlFor={id} style={{ color: theme.text, fontSize: '0.9rem', cursor: 'pointer' }}>
        {label}
      </label>
    </div>
  );

  return (
    <>
      {showLegal && <LegalModal onAccept={() => setShowLegal(false)} />}
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}

      {/* Delete All Confirmation Popup */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 3000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: theme.cardBackground, borderRadius: '16px',
            width: '100%', maxWidth: '380px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden'
          }}>
            <div style={{
              backgroundColor: '#7B0000', padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              <span style={{ fontSize: '1.4rem' }}>⚠️</span>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '1.05rem', fontWeight: '800' }}>
                Delete Entire Checklist
              </h2>
            </div>
            <div style={{ padding: '24px 20px' }}>
              <p style={{ color: theme.text, fontSize: '0.95rem', marginBottom: '8px', fontWeight: '700' }}>
                This will permanently delete:
              </p>
              <ul style={{ color: theme.subText, fontSize: '0.88rem', paddingLeft: '18px', marginBottom: '20px', lineHeight: '1.8' }}>
                <li>All visited place history</li>
                <li>All scheduled dates</li>
                <li>All manually added places</li>
              </ul>
              <p style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: '700', marginBottom: '20px' }}>
                This action cannot be undone. Are you sure?
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '8px',
                    border: `2px solid ${theme.border}`, backgroundColor: 'transparent',
                    color: theme.text, fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem'
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllConfirm}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                    backgroundColor: '#ef4444', color: '#fff',
                    fontWeight: '800', cursor: 'pointer', fontSize: '0.9rem'
                  }}>
                  Yes, Delete All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Manually Modal */}
      {showManual && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: theme.cardBackground, borderRadius: '16px',
            width: '100%', maxWidth: '480px', maxHeight: '90vh',
            overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}>
            <div style={{
              backgroundColor: theme.headerBackground, padding: '16px 20px',
              borderRadius: '16px 16px 0 0', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center',
              position: 'sticky', top: 0, zIndex: 1
            }}>
              <h2 style={{ color: theme.headerText, margin: 0, fontSize: '1.1rem' }}>Add a Place</h2>
              <button onClick={() => setShowManual(false)} style={{
                background: 'none', border: 'none',
                color: theme.headerText, fontSize: '1.5rem', cursor: 'pointer'
              }}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              {error && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '10px' }}>{error}</p>}
              <input placeholder="Place Name *" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
              <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
                <option value="">Select Category *</option>
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <input placeholder="Address (optional)" value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
              <input placeholder="Directions hint (optional)" value={directions} onChange={(e) => setDirections(e.target.value)} style={inputStyle} />
              <textarea placeholder="Description / Notes (optional)" value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowManual(false)} style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: `2px solid ${theme.border}`, backgroundColor: 'transparent',
                  color: theme.text, fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem'
                }}>Cancel</button>
                <button onClick={handleAddPlace} style={{
                  flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
                  backgroundColor: saving ? theme.border : theme.buttonPrimary,
                  color: theme.buttonText, fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9rem'
                }}>{saving ? 'Saving...' : 'Save Place'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="hamburger-wrapper" ref={wrapperRef}>
        <button className="hamburger-btn" onClick={() => setOpen(!open)}>
          <span /><span /><span />
        </button>

        {open && (
          <div className="menu-drawer" style={{
            backgroundColor: theme.cardBackground, color: theme.text,
            border: `1px solid ${theme.border}`, maxHeight: '80vh', overflowY: 'auto',
          }}>
            <h3 className="menu-section-title" style={{ color: theme.buttonPrimary }}>Filter & Sort</h3>

            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}
              className="menu-select"
              style={{ backgroundColor: theme.background, color: theme.text, borderColor: theme.border }}>
              <option value="">All Places</option>
              <option value="visited">Visited</option>
              <option value="unvisited">Unvisited</option>
            </select>

            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
              className="menu-select"
              style={{ backgroundColor: theme.background, color: theme.text, borderColor: theme.border }}>
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>

            {!isGuest && checkboxRow('myPlaces', myPlacesOnly, (e) => setMyPlacesOnly(e.target.checked), 'My Added Places Only')}
            {checkboxRow('sortCat', sortByCategory, (e) => setSortByCategory(e.target.checked), 'Sort by Category A–Z')}

            <button className="menu-btn apply-btn" onClick={handleSubmit}
              style={{ backgroundColor: theme.buttonPrimary, color: theme.buttonText }}>
              Apply Filter
            </button>

            <hr className="menu-divider" style={{ borderColor: theme.border }} />

            {!isGuest && (
              <button className="menu-btn" onClick={() => { setShowManual(true); setOpen(false); }}
                style={{ backgroundColor: theme.border, color: theme.text }}>
                Add Manually
              </button>
            )}

            {!isGuest && (
              <button className="menu-btn" onClick={() => { setShowProfile(true); setOpen(false); }}
                style={{ backgroundColor: theme.border, color: theme.text }}>
                My Profile
              </button>
            )}

            <hr className="menu-divider" style={{ borderColor: theme.border }} />

            <button className="menu-btn" onClick={() => setShowThemes(!showThemes)}
              style={{ backgroundColor: theme.border, color: theme.text }}>
              {showThemes ? 'Hide Themes' : 'Change Theme'}
            </button>

            {showThemes && <ThemeSelector />}

            <hr className="menu-divider" style={{ borderColor: theme.border }} />

            <button className="menu-btn reset-btn" onClick={handleReset}
              style={{ backgroundColor: '#FFD700', color: '#000000' }}>
              Reset Checklist
            </button>

            {!isGuest && (
              <button className="menu-btn" onClick={handleRestore}
                style={{ backgroundColor: theme.border, color: theme.text }}>
                Restore My Added Places
              </button>
            )}

            {!isGuest && (
              <button className="menu-btn" onClick={() => { setShowDeleteConfirm(true); setOpen(false); }}
                style={{ backgroundColor: '#ef4444', color: '#fff', fontWeight: '800' }}>
                🗑️ Delete All
              </button>
            )}

            <button className="menu-btn" onClick={() => { setShowLegal(true); setOpen(false); }}
              style={{ backgroundColor: theme.border, color: theme.text }}>
              Terms & Privacy
            </button>

            {isGuest ? (
              <button className="menu-btn logout-btn" onClick={() => { window.location.href = '/login'; }}
                style={{ backgroundColor: '#C41E3A', color: '#fff' }}>
                Sign Up / Login
              </button>
            ) : (
              <button className="menu-btn logout-btn" onClick={logout}
                style={{ backgroundColor: '#333', color: '#fff' }}>
                Logout
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default HamburgerMenu;